import { Suspense, lazy, useContext } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
import { IS_SUBSCRIPTION_ENABLED, IS_ORDER_PICKING_ENABLED } from 'config/flags';
// layouts
import DashboardLayout from '../layouts/dashboard';
import PublicLayout from '../layouts/public';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// components
import LoadingScreen from '../components/LoadingScreen';
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import SubscriptionProductPicker from '../pages/public/subscription/pick-products';
import { AuthContext } from '../contexts/JWTContext';
import useOrderInfoCleanup from '../hooks/useOrderInfoCleanUp';
import { PATH_DASHBOARD, PATH_AUTH } from './paths';
// ----------------------------------------------------------------------

const Loadable = (Component: any) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes(PATH_DASHBOARD.root);

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const { user } = useContext(AuthContext);

  useOrderInfoCleanup();

  const routes = PROTECTED_ROUTES.flatMap((route) => route ?? [])
    .filter((route) => (!user || !user.routes ? false : user.routes[`/${route.path}`]))
    .map((route) => {
      if (user) {
        const { routes } = user;
        const currentRoute = routes[`/${route.path}`];

        // display any screen
        if (currentRoute === '*') {
          return route;
        }

        route.children = route.children.filter((childRoute) =>
          childRoute.path
            ? (currentRoute as { [key: string]: boolean | string })[childRoute.path]
            : true
        );

        return route;
      }

      return route;
    });

  const applicationRoutes = [
    ...routes,
    // public subscription pages
    {
      path: PATH_DASHBOARD.public_subscription.root,
      element: <PublicLayout />,
      children: [
        {
          index: true,
          element: <Navigate to={PATH_DASHBOARD.public_subscription.chooseProducts} replace />
        },
        {
          path: PATH_DASHBOARD.public_subscription.chooseProducts,
          element: <SubscriptionProductPicker />
        }
      ]
    },
    // Auth Routes
    {
      path: 'auth',
      children: [
        {
          index: true,
          element: <Navigate to={PATH_AUTH.login} replace />
        },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        }
      ]
    },
    {
      path: '/',
      children: [
        {
          path: '/',
          element: (
            <AuthGuard>
              <Navigate to={PATH_DASHBOARD.root} replace />
            </AuthGuard>
          )
        }
      ]
    },
    // Main Routes
    {
      path: '*',
      element: (
        <AuthGuard>
          <LogoOnlyLayout />
        </AuthGuard>
      ),
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    }
  ];

  return useRoutes(applicationRoutes);
}

// IMPORT COMPONENTS

// Auth
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
// User
const UserList = Loadable(lazy(() => import('../pages/users/UserList')));
const UserCreate = Loadable(lazy(() => import('../pages/users/UserCreate')));
const UserProfile = Loadable(lazy(() => import('../pages/users/UserProfile')));
// Orders
const OrderList = Loadable(lazy(() => import('../pages/orders/OrderList')));
const OrderClient = Loadable(lazy(() => import('../pages/orders/OrderClient')));
const OrderForm = Loadable(lazy(() => import('../pages/orders/OrderForm')));
const OrderFormEdit = Loadable(lazy(() => import('../pages/orders/OrderFormEdit')));
const OrderPicking = Loadable(lazy(() => import('../pages/orders/OrderPicking')));
// Notifications
const NotificationsList = Loadable(lazy(() => import('../pages/notifications/NotificationsList')));
// History
const HistoryList = Loadable(lazy(() => import('../pages/history/HistoryList')));

// Dashboard
const Dashboard = Loadable(lazy(() => import('../pages/dashboard')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));

// subscription
const Subscription = Loadable(lazy(() => import('../pages/subscription')));

type RouteRecord = {
  index?: boolean;
  path?: string;
  element: JSX.Element;
};

type RouteType = RouteRecord & {
  children: RouteRecord[];
};

const PROTECTED_ROUTES: Array<RouteType | undefined> = [
  // Dashboard Routes
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [{ index: true, element: <Dashboard /> }]
  },
  {
    path: 'order',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <Navigate to={PATH_DASHBOARD.orders.list} replace /> },
      { path: 'list', element: <OrderList /> },
      { path: 'client', element: <OrderClient /> },
      { path: 'create', element: <OrderForm /> },
      { path: ':id/edit', element: <OrderFormEdit /> },
      IS_ORDER_PICKING_ENABLED
        ? ({ path: ':id/picking', element: <OrderPicking /> } as RouteRecord)
        : undefined
    ].filter((entry): entry is RouteRecord => Boolean(entry))
  },
  {
    path: 'notifications',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <Navigate to={PATH_DASHBOARD.notifications.list} replace /> },
      { path: 'list', element: <NotificationsList /> }
    ]
  },
  {
    path: 'history',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <Navigate to={PATH_DASHBOARD.history.list} replace /> },
      { path: 'list', element: <HistoryList /> }
    ]
  },
  {
    path: 'user',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: <Navigate to={PATH_DASHBOARD.user.list} replace /> },
      { path: 'list', element: <UserList /> },
      { path: 'profile', element: <UserProfile /> },
      { path: 'create', element: <UserCreate /> },
      { path: ':id/edit', element: <UserCreate /> }
    ]
  },
  IS_SUBSCRIPTION_ENABLED
    ? {
        path: PATH_DASHBOARD.subscriptions.root,
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          { index: true, element: <Navigate to={PATH_DASHBOARD.subscriptions.list} replace /> },
          { path: 'list', element: <Subscription /> }
        ]
      }
    : undefined
];
